import { AlertTriangle, Info } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type {
  CohortBasis,
  CohortStageValue,
  PerformanceLoanDetail,
  PipelinePerformanceViewModel
} from './types';
import { formatCompactCurrency, formatCurrency, formatPercent } from './formatters';

interface PerformanceCohortsProps {
  model: PipelinePerformanceViewModel;
  basis: CohortBasis;
  onBasisChange: (basis: CohortBasis) => void;
  onOpenLoans: (title: string, description: string, loans: PerformanceLoanDetail[]) => void;
}

function StageCell({
  label,
  period,
  value,
  onOpenLoans,
  showLabel = false
}: {
  label: string;
  period: string;
  value: CohortStageValue;
  onOpenLoans: PerformanceCohortsProps['onOpenLoans'];
  showLabel?: boolean;
}) {
  return (
    <button
      type="button"
      className="rounded px-1.5 py-1 text-right hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      onClick={() => onOpenLoans(
        `${period} · ${label}`,
        `${value.units.toLocaleString()} loans · ${formatCurrency(value.volume)}`,
        value.loans
      )}
    >
      {showLabel && <span className="block text-left text-xs text-muted-foreground">{label}</span>}
      <span className="block font-medium tabular-nums">{value.units.toLocaleString()}</span>
      <span className="block text-[11px] text-muted-foreground tabular-nums">
        {formatPercent(value.percentage, false)} · {formatCompactCurrency(value.volume)}
      </span>
    </button>
  );
}

export function PerformanceCohorts({
  model,
  basis,
  onBasisChange,
  onOpenLoans
}: PerformanceCohortsProps) {
  const series = model.cohorts[basis];
  const basisLabel = basis === 'application' ? 'Application' : 'Created';

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 rounded-lg border bg-muted/20 p-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="text-sm font-medium">Cohort entry event</div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Applications are the default production cohort. Created shows every new Encompass loan record.
          </p>
        </div>
        <div className="flex w-fit rounded-md border bg-background p-0.5" aria-label="Cohort entry event">
          <Button
            type="button"
            size="sm"
            variant={basis === 'application' ? 'secondary' : 'ghost'}
            className="h-7 text-xs"
            aria-pressed={basis === 'application'}
            onClick={() => onBasisChange('application')}
          >
            Applications
          </Button>
          <Button
            type="button"
            size="sm"
            variant={basis === 'created' ? 'secondary' : 'ghost'}
            className="h-7 text-xs"
            aria-pressed={basis === 'created'}
            onClick={() => onBasisChange('created')}
          >
            Created
          </Button>
        </div>
      </div>

      {series.anomalyCount > 0 && (
        <div className="flex gap-2 rounded-lg border border-warning/30 bg-warning/5 px-3 py-2 text-xs">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
          <span>
            {series.anomalyCount.toLocaleString()} downstream event{series.anomalyCount === 1 ? '' : 's'} occurred before its cohort date and {series.anomalyCount === 1 ? 'was' : 'were'} excluded from progression.
          </span>
        </div>
      )}

      <Card className="shadow-sm">
        <CardHeader className="p-4 pb-2">
          <CardTitle className="text-base">{basisLabel} cohort progression</CardTitle>
          <div className="flex items-start gap-1.5 text-xs text-muted-foreground">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <p>Application, lock, and funded are independent dated events on or after cohort entry. Application receipt does not imply an Active folder move.</p>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="space-y-2 p-3 md:hidden">
            {series.rows.map((row) => (
              <div key={row.key} className="rounded-lg border p-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="font-medium">{row.label}</span>
                  {row.anomalyCount > 0 && (
                    <Badge variant="outline" className="border-warning/30 text-[10px] text-warning">
                      {row.anomalyCount} excluded
                    </Badge>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <StageCell showLabel label="Cohort loans" period={row.label} value={row.cohort} onOpenLoans={onOpenLoans} />
                  <StageCell showLabel label="Applications" period={row.label} value={row.application} onOpenLoans={onOpenLoans} />
                  <StageCell showLabel label="Locked" period={row.label} value={row.locked} onOpenLoans={onOpenLoans} />
                  <StageCell showLabel label="Funded" period={row.label} value={row.funded} onOpenLoans={onOpenLoans} />
                </div>
              </div>
            ))}
            {series.rows.length === 0 && (
              <div className="py-10 text-center text-sm text-muted-foreground">No cohort entries in this range.</div>
            )}
          </div>
          <div className="hidden overflow-x-auto md:block">
          <table className="w-full min-w-[720px] text-sm">
            <caption className="sr-only">{basisLabel} cohort cumulative event progression</caption>
            <thead>
              <tr className="border-y bg-muted/30 text-left text-xs text-muted-foreground">
                <th className="px-4 py-2.5 font-medium">Cohort period</th>
                <th className="px-3 py-2.5 text-right font-medium">Cohort size</th>
                <th className="px-3 py-2.5 text-right font-medium">Applications</th>
                <th className="px-3 py-2.5 text-right font-medium">Locked</th>
                <th className="px-3 py-2.5 text-right font-medium">Funded</th>
              </tr>
            </thead>
            <tbody>
              {series.rows.map((row) => (
                <tr key={row.key} className="border-b last:border-0 hover:bg-muted/20">
                  <td className="px-4 py-3">
                    <div className="font-medium">{row.label}</div>
                    {row.anomalyCount > 0 && (
                      <Badge variant="outline" className="mt-1 border-warning/30 text-[10px] text-warning">
                        {row.anomalyCount} excluded anomal{row.anomalyCount === 1 ? 'y' : 'ies'}
                      </Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <StageCell label="Cohort loans" period={row.label} value={row.cohort} onOpenLoans={onOpenLoans} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <StageCell label="Applications" period={row.label} value={row.application} onOpenLoans={onOpenLoans} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <StageCell label="Locked" period={row.label} value={row.locked} onOpenLoans={onOpenLoans} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <StageCell label="Funded" period={row.label} value={row.funded} onOpenLoans={onOpenLoans} />
                  </td>
                </tr>
              ))}
              {series.rows.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">No cohort entries in this range.</td></tr>
              )}
            </tbody>
          </table>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardHeader className="p-4 pb-2">
          <CardTitle className="text-base">Current folder distribution</CardTitle>
          <p className="text-xs text-muted-foreground">Where loans from the selected {basisLabel.toLowerCase()} cohorts are now. This is a current-state lens, separate from dated progression.</p>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y md:hidden">
            {series.folderDistribution.map((folder) => (
              <button
                key={folder.folder}
                type="button"
                className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                onClick={() => onOpenLoans(
                  `${folder.folder} cohort loans`,
                  `Current folder · ${formatPercent(folder.percentage, false)} of selected cohort loans`,
                  folder.loans
                )}
              >
                <span className="font-medium">{folder.folder}</span>
                <span className="text-right text-sm tabular-nums">
                  <span className="block font-medium">{folder.units.toLocaleString()} · {formatCompactCurrency(folder.volume)}</span>
                  <span className="block text-xs text-muted-foreground">{formatPercent(folder.percentage, false)}</span>
                </span>
              </button>
            ))}
            {series.folderDistribution.length === 0 && (
              <div className="py-10 text-center text-sm text-muted-foreground">No cohort loans in this range.</div>
            )}
          </div>
          <div className="hidden overflow-x-auto md:block">
          <table className="w-full min-w-[560px] text-sm">
            <caption className="sr-only">Current folder distribution for selected cohorts</caption>
            <thead>
              <tr className="border-y bg-muted/30 text-left text-xs text-muted-foreground">
                <th className="px-4 py-2.5 font-medium">Current folder</th>
                <th className="px-3 py-2.5 text-right font-medium">Loans</th>
                <th className="px-3 py-2.5 text-right font-medium">Share</th>
                <th className="px-4 py-2.5 text-right font-medium">Volume</th>
              </tr>
            </thead>
            <tbody>
              {series.folderDistribution.map((folder) => (
                <tr key={folder.folder} className="border-b last:border-0 hover:bg-muted/20">
                  <td className="px-4 py-3 font-medium">{folder.folder}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      className="rounded px-1.5 py-1 font-medium tabular-nums hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      onClick={() => onOpenLoans(
                        `${folder.folder} cohort loans`,
                        `Current folder · ${formatPercent(folder.percentage, false)} of selected cohort loans`,
                        folder.loans
                      )}
                    >
                      {folder.units.toLocaleString()}
                    </button>
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums">{formatPercent(folder.percentage, false)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatCompactCurrency(folder.volume)}</td>
                </tr>
              ))}
              {series.folderDistribution.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-10 text-center text-muted-foreground">No cohort loans in this range.</td></tr>
              )}
            </tbody>
          </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
